import { z } from "zod";

const id = z.string().min(1).max(128).regex(/^[a-zA-Z0-9_-]+$/);
const shortText = z.string().trim().min(1).max(240);
export const zCastQuestion = z.object({
  id,
  characterId: id,
  kind: z.enum(["remember", "imagine"]),
  question: shortText,
  choices: z.array(shortText).min(2).max(4),
  // Imagination questions have no correct answer; votes never become canon.
  correctChoice: z.number().int().min(0).max(3).nullable(),
  explanation: shortText,
}).strict();
export type CastQuestion = z.infer<typeof zCastQuestion>;
export const zCastJoke = z.object({
  id,
  characterId: id,
  setup: shortText,
  punchline: shortText,
}).strict();
export const zSceneActivityContent = z.object({
  questions: z.array(zCastQuestion).min(2).max(4),
  jokes: z.array(zCastJoke).min(2).max(4),
}).strict();
export type SceneActivityContent = z.infer<typeof zSceneActivityContent>;
export const zPreparedSceneActivities = z.object({
  status: z.enum(["pending", "generating", "ready", "failed"]),
  sceneId: id,
  content: zSceneActivityContent.optional(),
  model: z.string().max(100).optional(),
  preparedAt: z.number().optional(),
  requestedAt: z.number(),
  error: z.string().max(500).optional(),
});
export type PreparedSceneActivities = z.infer<typeof zPreparedSceneActivities>;
export interface StoryActivitySource { accountId: string; performanceId: string; sceneId: string }
export type WaitingActivityMode = "quiz" | "jokes";
export interface ShowActivity {
  id: string;
  source: StoryActivitySource;
  mode: WaitingActivityMode;
  status: "active" | "ended";
  questionIndex: number;
  questionCount: number;
  question: Omit<CastQuestion, "correctChoice" | "explanation">;
  revealed: boolean;
  correctChoice?: number | null;
  explanation?: string;
  counts: number[];
  totalVotes: number;
  updatedAt: number;
}
export interface ShowActivityAnswer {
  questionId: string;
  uid: string;
  characterId: string;
  choice: number;
  performerName: string;
  answeredAt: number;
}
export type ManageStoryActivitiesRequest = StoryActivitySource & { action: "get" | "prepare" };
export interface ManageStoryActivitiesResponse { activities?: PreparedSceneActivities }
export type ManageShowActivityRequest =
  | { action: "start"; sessionId: string; source: StoryActivitySource; mode: WaitingActivityMode }
  | { action: "get"; sessionId: string }
  | { action: "vote"; sessionId: string; activityId: string; questionId: string; characterId: string; choice: number }
  | { action: "reveal" | "next" | "end"; sessionId: string; activityId: string; questionId: string };
export interface ManageShowActivityResponse { activity?: ShowActivity; answer?: ShowActivityAnswer; answers?: ShowActivityAnswer[] }
